import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function Quiz({ userData, onComplete }) {
  const [questions, setQuestions]   = useState(null);
  const [answers, setAnswers]       = useState({});
  const [result, setResult]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [passThreshold, setPassThreshold] = useState(0.8);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/api/quiz', { params: { role: userData.role } });
        if (!cancelled) {
          setQuestions(data.questions);
          setPassThreshold(data.passThreshold ?? 0.8);
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'Could not load quiz.');
      }
    })();
    return () => { cancelled = true; };
  }, [userData.role]);

  const handleSelect = (qi, oi) => {
    if (result) return;
    setAnswers((a) => ({ ...a, [qi]: oi }));
  };

  const allAnswered = questions && questions.every((_, i) => answers[i] !== undefined);

  const handleSubmit = async () => {
    if (!allAnswered || loading) return;
    setLoading(true);
    setError('');
    try {
      const orderedAnswers = questions.map((_, i) => answers[i]);
      const { data } = await axios.post('/api/quiz/grade', {
        role: userData.role,
        answers: orderedAnswers,
      });
      setResult(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetake = () => {
    setAnswers({});
    setResult(null);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContinue = () => {
    onComplete({
      quizScore: result.score,
      quizTotal: result.total,
      quizPercent: result.percent,
      quizPassedAt: new Date().toISOString(),
    });
  };

  if (error && !questions) {
    return (
      <div className="page-container">
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <h2 style={{ color: '#C53030', marginBottom: 12 }}>Quiz unavailable</h2>
          <p style={{ color: '#546E7A' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!questions) {
    return (
      <div className="page-container">
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: '#546E7A' }}>Loading your quiz…</p>
        </div>
      </div>
    );
  }

  const answeredCount = Object.keys(answers).length;
  const totalQuestions = questions.length;
  const passing = result?.passing;
  const percentStr = result ? Math.round(result.percent * 100) : 0;
  const thresholdStr = Math.round(passThreshold * 100);

  return (
    <div className="page-container">
      <div style={{ marginBottom: 24 }}>
        <div className="step-badge"><span className="step-badge-dot" />Step 3 of 5 — Knowledge Check</div>
        <h1 className="page-title">Role-Specific Quiz</h1>
        <p className="page-subtitle">
          {result
            ? (passing
                ? 'You passed. Click Continue to acknowledge your training.'
                : `You need ${thresholdStr}% to pass. Review the highlighted answers, then retake.`)
            : <>Answer all {totalQuestions} questions for your role: <strong>{userData.role}</strong>. You need {thresholdStr}% to pass.</>
          }
        </p>
      </div>

      {result && (
        <div
          className="card"
          style={{
            textAlign: 'center',
            padding: 28,
            marginBottom: 24,
            background: passing ? '#E8F8F0' : '#FFF5F5',
            border: `1.5px solid ${passing ? '#37C97D' : '#FEB2B2'}`,
          }}
        >
          <div style={{ fontSize: 36, marginBottom: 8 }}>{passing ? '✅' : '⚠️'}</div>
          <h2 style={{ color: passing ? '#1B7E4A' : '#C53030', fontSize: 22, marginBottom: 4 }}>
            {passing ? 'Passed!' : 'Did not pass'}
          </h2>
          <p style={{ color: '#2C3E50', fontSize: 16, marginBottom: 18 }}>
            <strong>{result.score} / {result.total}</strong> correct ({percentStr}%)
          </p>
          {passing ? (
            <button className="btn btn-primary btn--lg" onClick={handleContinue}>
              Continue to Acknowledgment →
            </button>
          ) : (
            <button className="btn btn-primary btn--lg" onClick={handleRetake}>
              Retake Quiz
            </button>
          )}
        </div>
      )}

      <div className="card" style={{ padding: '24px 28px' }}>
        {questions.map((q, qi) => {
          const userAnswer = answers[qi];
          const correctAnswer = result?.perQuestion?.[qi]?.correctAnswer;
          const questionCorrect = result?.perQuestion?.[qi]?.correct;

          return (
            <div
              key={qi}
              style={{
                marginBottom: qi === questions.length - 1 ? 0 : 28,
                paddingBottom: qi === questions.length - 1 ? 0 : 24,
                borderBottom: qi === questions.length - 1 ? 'none' : '1px solid #EEF3F8',
              }}
            >
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <span
                  style={{
                    flexShrink: 0,
                    background: '#1B3A5C',
                    color: '#fff',
                    width: 26,
                    height: 26,
                    borderRadius: 13,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {qi + 1}
                </span>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#1B3A5C', lineHeight: 1.45 }}>
                  {q.q}
                  {result && (
                    <span
                      style={{
                        marginLeft: 10,
                        fontSize: 12,
                        fontWeight: 700,
                        color: questionCorrect ? '#1B7E4A' : '#C53030',
                      }}
                    >
                      {questionCorrect ? '✓ Correct' : '✗ Incorrect'}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginLeft: 38 }}>
                {q.options.map((opt, oi) => {
                  const selected = userAnswer === oi;
                  const showCorrect = result && oi === correctAnswer;
                  const showUserWrong = result && selected && !questionCorrect;

                  let background = '#fff';
                  let borderColor = '#DDE3ED';
                  let color = '#2C3E50';

                  if (showCorrect) {
                    background = '#E8F8F0';
                    borderColor = '#37C97D';
                    color = '#1B7E4A';
                  } else if (showUserWrong) {
                    background = '#FFF5F5';
                    borderColor = '#FEB2B2';
                    color = '#C53030';
                  } else if (selected) {
                    background = '#EEF3F8';
                    borderColor = '#1B3A5C';
                  }

                  return (
                    <label
                      key={oi}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '10px 14px',
                        marginBottom: 8,
                        border: `1.5px solid ${borderColor}`,
                        borderRadius: 8,
                        background,
                        color,
                        cursor: result ? 'default' : 'pointer',
                        transition: 'border-color 120ms, background 120ms',
                        fontSize: 14,
                        lineHeight: 1.45,
                      }}
                    >
                      <input
                        type="radio"
                        name={`q-${qi}`}
                        checked={selected}
                        onChange={() => handleSelect(qi, oi)}
                        disabled={!!result}
                        style={{ marginTop: 3 }}
                      />
                      <span>{opt}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!result && (
        <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1, fontSize: 13, color: '#546E7A' }}>
            {answeredCount} of {totalQuestions} answered
          </div>
          <button
            className="btn btn-primary btn--lg"
            onClick={handleSubmit}
            disabled={!allAnswered || loading}
          >
            {loading ? 'Grading…' : 'Submit Quiz'}
          </button>
        </div>
      )}

      {error && (
        <p style={{ color: '#C53030', fontSize: 13, marginTop: 12, textAlign: 'right' }}>{error}</p>
      )}
    </div>
  );
}
